import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { useState, useRef, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { SizeTransition } from '@utils/react';
import { useRearrangeBlockMutation, rearrangeBlocks } from '../../../../api/mutation.rearrange-blocks.js';
import { Block } from './block.js';
import { scrollWrapperCls, scrollContainerCls, listCls, transitionableBlockCls, blockTransitionDurationMs } from './block-list.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function BlockList(props) {
  const rearrange = useRearrangeBlockMutation();

  // Unfortunately react-query onMutate() functions are not immediately
  // synchronous after a mutation has been called. This means that after a
  // drag-n-drop has been finished a brief flicker from the old state to the
  // optimistic update will be seen without further changes.
  // Thus we temporarily set up a reordered course synchronously when drag ends
  // and as soon as the mutation has been applied, then the default course will
  // be used again.
  const [immediatelyReorderedCourse, setImmediatelyReoredredCourse] = useState();
  const course = immediatelyReorderedCourse ?? props.course;
  const handleDragStart = () => {
    // Expose that drag has been enabled such that Asides can stop using
    // transform: translate() which otherwise breaks react-beautiful-dnd
    document.documentElement.classList.add("drag-active");
  };
  const handleDragEnd = result => {
    if (result.destination) {
      setImmediatelyReoredredCourse(rearrangeBlocks({
        course: props.course,
        indices: {
          from: result.source.index,
          to: result.destination.index
        }
      }));
      rearrange.mutate({
        course: props.course,
        indices: {
          from: result.source.index,
          to: result.destination.index
        }
      });
      setTimeout(() => {
        setImmediatelyReoredredCourse(undefined);
      });
    }

    // Notify that the drag event is ending such that the Aside can restore
    // itself.
    document.documentElement.classList.add("drag-pending-exit");
    document.documentElement.classList.remove("drag-active");
    setTimeout(() => {
      document.documentElement.classList.remove("drag-pending-exit");
    });
  };
  useAutoFocusOnChange(course?.blocks ?? []);
  return /*#__PURE__*/jsx(DragDropContext, {
    onBeforeDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    children: /*#__PURE__*/jsx("div", {
      className: scrollWrapperCls,
      children: /*#__PURE__*/jsx("div", {
        className: scrollContainerCls,
        children: /*#__PURE__*/jsx(Droppable, {
          droppableId: "block-list",
          children: (provided, snapshot) => /*#__PURE__*/jsxs("ol", {
            ...provided.droppableProps,
            ref: provided.innerRef,
            className: listCls,
            children: [/*#__PURE__*/jsx(TransitionGroup, {
              component: null,
              children: course?.blocks?.map((block, index) => /*#__PURE__*/jsx(SizeTransition, {
                axis: "vertical",
                timeout: blockTransitionDurationMs,
                classNames: transitionableBlockCls,
                mountOnEnter: true,
                unmountOnExit: true,
                children: ref => /*#__PURE__*/jsx("li", {
                  ref: ref,
                  className: transitionableBlockCls,
                  "data-block": block.id,
                  children: /*#__PURE__*/jsx(Block, {
                    block: block,
                    course: props.course,
                    index: index,
                    isDragActive: snapshot.isDraggingOver
                  })
                })
              }, block.id))
            }), provided.placeholder]
          })
        })
      })
    })
  });
}
function useAutoFocusOnChange(blocks) {
  const prevBlocks = useRef(blocks);
  const blockIds = blocks.map(block => block.id);
  useEffect(() => {
    const prevBlockIds = prevBlocks.current.map(block => block.id);

    // We only want to trigger auto-focus once an item has been added to the
    // list of blocks, not when they're removed.
    if (blockIds.length <= prevBlockIds.length) {
      prevBlocks.current = blocks;
      return;
    }
    const changedBlockIds = diff(blockIds, prevBlockIds);
    if (changedBlockIds.length !== 1) {
      return;
    }
    const changedBlockId = changedBlockIds[0];
    const changedDOMBlock = document.querySelector(`[data-block="${changedBlockId}"]`);

    // Defer scrolling until the element actually is its full height, else the
    // scrolling will fail!
    setTimeout(() => {
      changedDOMBlock?.scrollIntoView({
        block: "center",
        behavior: "smooth"
      });
    }, blockTransitionDurationMs);

    // Now that we've completed our action, update the previous blocks
    prevBlocks.current = blocks;

    // We only need to re-run the auto-focus on the list of blocks actually has
    // changed!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockIds.join("-")]);
}

/**
 * Finds the symmetric difference between two arrays, inspired by
 * https://stackoverflow.com/questions/1187518/how-to-get-the-difference-between-two-arrays-in-javascript
 */
function diff(arr1, arr2) {
  return arr1.filter(x => !arr2.includes(x)).concat(arr2.filter(x => !arr1.includes(x)));
}

export { BlockList };
//# sourceMappingURL=block-list.js.map
